import { useCallback, useState } from 'react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { useAddRestrictedWord } from '@/modules/restricted-words/hooks/use-add-restricted-word.ts'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  open: boolean
  onClose: () => void
}

export const AddRestrictedWordDialog = ({ open, onClose }: Props) => {
  const [word, setWord] = useState('')
  const [blockAllCombinations, setBlockAllCombinations] = useState(false)
  const toast = useToast()
  const [warningOpen, setWarningOpen] = useState(false)

  const { addRestrictedWord, isAddRestrictedWordLoading } =
    useAddRestrictedWord()

  const handleClose = useCallback(() => {
    onClose()
    setWord('')
    setBlockAllCombinations(false)
  }, [onClose])

  const handleAddClick = useCallback(() => {
    addRestrictedWord({ word, blockAllCombinations })
      .then(() => {
        toast.success('New blacklisted word has been added successfully')
        handleClose()
      })
      .catch((error) => {
        handleError(error, 'Failed to add new word, please try again later')
      })
  }, [addRestrictedWord, blockAllCombinations, handleClose, toast, word])

  const isUsedWrong = word.includes('*') && word.includes(' ')

  return (
    <Dialog show={open}>
      <Dialog.Header>
        <Dialog.Title>Add Blacklisted Word</Dialog.Title>
      </Dialog.Header>

      <div className="mt-5">
        <div className="mb-1 flex items-center">
          <p className="mr-1.5 text-sm font-medium">Blacklisted Word</p>
          <Tooltip
            open={warningOpen}
            side="right"
            contentClassName="max-w-[400px] text-start"
            content={
              <p className="text-xs font-medium">
                If you want to match words starting or ending with certain
                letters you can use the * character
              </p>
            }
          >
            <InfoIcon
              onMouseLeave={() => setWarningOpen(false)}
              onMouseEnter={() => setWarningOpen(true)}
            />
          </Tooltip>
        </div>
        <Tooltip
          open={isUsedWrong}
          contentClassName="max-w-[400px] text-start"
          className="w-full"
          content={
            <p className="text-xs font-medium">
              This function only works with single-word inputs and does not
              support inputs with spaces.
            </p>
          }
        >
          <TextField
            placeholder="Enter your words"
            className={classNames({
              'border border-warning-dark focus-within:border-warning-dark':
                isUsedWrong,
            })}
            value={word}
            onChange={(event) => setWord(event.target.value)}
          />
        </Tooltip>
        <div className="mt-1.5 flex items-center">
          <Checkbox
            checked={blockAllCombinations}
            onCheckedChange={(val) => setBlockAllCombinations(val as boolean)}
          />
          <p className="ml-1.5 text-xs text-text-sub">
            Block all combinations (Use with caution)
          </p>
        </div>
      </div>

      {blockAllCombinations && (
        <div className="mt-5 rounded-lg bg-secondary-50 p-3 text-xs dark:bg-secondary-600">
          This could cause false positives! Let&apos;s say you enable it on
          &#34;ko&#34;, this would block the word &#34;koala&#34;.
        </div>
      )}

      <Dialog.Footer className="gap-2">
        <Button
          disabled={isAddRestrictedWordLoading}
          className="flex-1"
          variant="outline"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          isLoading={isAddRestrictedWordLoading}
          className="flex-1"
          onClick={handleAddClick}
          disabled={!word}
        >
          Add Word
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
